/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Form, Input, Modal, Popconfirm, Switch, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import {
  getSponsoredPosts,
  createSponsoredPost,
  reorderSponsoredPosts,
  deleteSponsoredPost,
} from '@/Redux/slices/PostManagementSlice';
import { getPostDetail } from '@/Redux/slices/ModalSlice';
import UserRoleLabel from '@/Modules/userRoleLabel';
import { UserRoleEnums } from '@/Interfaces/I_userRole';
import PostDetailModal from '@/Modules/modal';
import { getFrontEndDomainByEnv } from '@/Axios/index';
import type { CmsSponsoredPostItem } from '@/Interfaces/I_postManagement';
import fileText from '../../images/icon/fileText.svg';

const MAX_POSTS = 5;

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(
            child as React.ReactElement,
            {},
            <MenuOutlined
              ref={setActivatorNodeRef}
              style={{ touchAction: 'none', cursor: 'move' }}
              {...listeners}
            />,
          );
        }
        return child;
      })}
    </tr>
  );
};

const SponsoredPost = ({ tabActive }: { tabActive: boolean }) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.postManagement.loading);
  const sponsoredPosts = useAppSelector((state) => state.postManagement.value.sponsoredPosts);

  const [items, setItems] = useState<CmsSponsoredPostItem[]>([]);
  const [pristine, setPristine] = useState(true);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [postDetailModalVisible, setPostDetailModalVisible] = useState(false);
  const [form] = Form.useForm();

  const initItems = useCallback(() => {
    setItems(sponsoredPosts ?? []);
    setPristine(true);
  }, [sponsoredPosts]);

  useEffect(() => {
    if (tabActive) {
      dispatch(getSponsoredPosts());
    }
  }, [dispatch, tabActive]);

  useEffect(() => {
    if (!loading && sponsoredPosts) {
      initItems();
    }
  }, [loading, sponsoredPosts, initItems]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setItems((prev) => {
        const activeIndex = prev.findIndex((i) => String(i.id) === active.id);
        const overIndex = prev.findIndex((i) => String(i.id) === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
      setPristine(false);
    }
  };

  const onSaveOrder = () => {
    dispatch(
      reorderSponsoredPosts({
        items: items.map((item, index) => ({ id: item.id, seq: index + 1 })),
      }),
    );
    setPristine(true);
  };

  const onAddPost = () => {
    const postUuid = form.getFieldValue('postUuid')?.trim();
    if (!postUuid) return;
    dispatch(createSponsoredPost({ postUuid, isAds: true }));
    form.resetFields();
    setAddModalVisible(false);
  };

  const onDelete = (id: number) => {
    dispatch(deleteSponsoredPost(id));
  };

  const openPostDetailModal = (postUuid: string) => {
    setPostDetailModalVisible(true);
    dispatch(getPostDetail(postUuid));
  };

  const canAddMore = items.length < MAX_POSTS;

  const columns: ColumnsType<CmsSponsoredPostItem> = [
    { key: 'sort', width: 48 },
    {
      title: 'Seq',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Post Title',
      dataIndex: 'title',
      render: (_, record) => (
        <Tooltip placement="leftTop" title={record.title}>
          <div className="flex items-center">
            <a
              href={`${getFrontEndDomainByEnv()}post/${record.postUuid}`}
              target="_blank"
              rel="noreferrer"
              className="line-clamp-3 break-all mb-0"
            >
              {record.title || record.postUuid}
            </a>
            <img
              aria-hidden
              src={fileText}
              alt="detail"
              className="cursor-pointer ml-2"
              onClick={() => openPostDetailModal(record.postUuid)}
            />
          </div>
        </Tooltip>
      ),
      width: '15%',
    },
    {
      title: 'Post Uuid',
      dataIndex: 'postUuid',
      render: (uuid: string) => (
        <Tooltip placement="leftTop" title={uuid}>
          {uuid}
        </Tooltip>
      ),
      width: '15%',
    },
    {
      title: 'User',
      render: (_, record) => {
        const userName = record.userName ?? record.user?.name ?? '-';
        return (
          <Tooltip placement="leftTop" title={userName}>
            <p className="h-6 line-clamp-1 break-all m-0">{userName}</p>
          </Tooltip>
        );
      },
    },
    {
      title: 'Pkey',
      render: (_, record) => record.userHybrisId ?? record.user?.hybrisId ?? '-',
    },
    {
      title: 'Topic',
      dataIndex: 'topic',
      width: '7%',
    },
    {
      title: 'User Role',
      dataIndex: ['user', 'kocUserRole'],
      render: (role: string) => <UserRoleLabel userRoleStr={role as UserRoleEnums} />,
      width: '5%',
    },
    {
      title: 'Ads',
      dataIndex: 'isAds',
      width: 70,
      render: (_: unknown, record: CmsSponsoredPostItem) => (record.isAds === true ? 'Yes' : 'No'),
    },
    {
      title: 'Disabled',
      dataIndex: 'disabled',
      render: (value: boolean) => <Switch disabled defaultChecked={value} />,
    },
    {
      title: 'Delete',
      key: 'delete',
      render: (_, record) => (
        <Popconfirm title="Remove this post from Carousel?" onConfirm={() => onDelete(record.id)} okText="Yes" cancelText="No">
          <DeleteOutlined className="cursor-pointer" />
        </Popconfirm>
      ),
      width: 80,
    },
  ];

  return (
    <>
      <div className="h-auto bg-white p-6 pb-2">
        <div className="flex items-center pb-4 justify-between">
          <div className="flex gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalVisible(true)}
              disabled={!canAddMore || loading}
            >
              Add Post
            </Button>
            {!canAddMore && (
              <span className="text-gray-500 text-sm self-center">(Max {MAX_POSTS} posts per Carousel)</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="primary" onClick={onSaveOrder} disabled={pristine || loading}>
              Save
            </Button>
            <Button onClick={initItems} disabled={pristine}>
              Reset
            </Button>
          </div>
        </div>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={items.map((i) => String(i.id))}
            strategy={verticalListSortingStrategy}
          >
            <Table
              components={{ body: { row: Row } }}
              rowKey={(record) => String(record.id)}
              columns={columns}
              dataSource={items}
              pagination={false}
              loading={loading}
            />
          </SortableContext>
        </DndContext>
      </div>

      <Modal
        title="Add Post to Carousel"
        open={addModalVisible}
        onOk={onAddPost}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        okText="Add"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <div className="flex gap-4 items-end">
            <Form.Item
              name="postUuid"
              label="Post UUID"
              rules={[{ required: true, message: 'Please enter Post UUID' }]}
              className="flex-1 mb-0"
            >
              <Input placeholder="Enter post UUID" />
            </Form.Item>
            <Form.Item label="Ads Post" className="mb-0">
              <span>Yes</span>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <PostDetailModal
        visible={postDetailModalVisible}
        onCancel={() => setPostDetailModalVisible(false)}
        modalType="postDetail"
      />
    </>
  );
};

export default SponsoredPost;
